/**
 * Deal Pipeline Page - 10-stage kanban board for Phase 5.
 * Cards are draggable between columns using native HTML5 drag-and-drop.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  Plus,
  X,
  GitMerge,
  BarChart3,
  Sparkles,
  Clock,
  ArrowRight,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealPipelineService from '../services/dealPipelineService';
import investorService from '../services/investorService';
import projectService from '../services/projectService';
import matchService from '../services/matchService';
import {
  DealPipeline,
  PipelineListFilters,
  PipelineOutcome,
  PipelineStage,
} from '../types/dealPipeline';
import { Investor } from '../types/investor';
import { Project } from '../types/project';
import { Match } from '../types/match';
import { PIPELINE_STAGES, STAGE_CONFIG } from '../components/PipelineStageBadge';

const OUTCOME_OPTIONS: Array<{ value: PipelineOutcome; label: string }> = [
  { value: 'funded', label: 'Funded' },
  { value: 'passed', label: 'Passed' },
  { value: 'stalled', label: 'Stalled' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const outcomeBadge = (outcome?: PipelineOutcome | null): string => {
  switch (outcome) {
    case 'funded':
      return 'bg-green-100 text-green-700';
    case 'passed':
      return 'bg-red-100 text-red-700';
    case 'stalled':
      return 'bg-amber-100 text-amber-700';
    case 'withdrawn':
      return 'bg-gray-100 text-gray-600';
    default:
      return '';
  }
};

const scoreColor = (score: number): string => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

const DealPipelinePage: React.FC = () => {
  const [pipelines, setPipelines] = useState<DealPipeline[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<PipelineListFilters>({});
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  // Drag state
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<PipelineStage | null>(null);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<number | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  const queryFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    const loadOptions = async () => {
      const [invRes, projRes] = await Promise.all([
        investorService.fetchInvestors({ size: 200 }),
        projectService.fetchProjects({ size: 200 }),
      ]);
      if (invRes.data) setInvestors(invRes.data.investors);
      if (projRes.data) setProjects(projRes.data.projects);
    };
    loadOptions();
  }, []);

  const loadPipelines = async () => {
    setIsLoading(true);
    const result = await dealPipelineService.fetchPipelines({
      ...queryFilters,
      size: 500,
    });
    if (result.error) {
      toast.error(result.error);
      setPipelines([]);
    } else {
      setPipelines(result.data?.pipelines || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadPipelines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilters]);

  const grouped = useMemo(() => {
    const map: Record<PipelineStage, DealPipeline[]> = {} as any;
    PIPELINE_STAGES.forEach((s) => {
      map[s] = [];
    });
    pipelines.forEach((p) => {
      if (map[p.stage]) {
        map[p.stage].push(p);
      } else {
        map[p.stage] = [p];
      }
    });
    return map;
  }, [pipelines]);

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e: React.DragEvent, stage: PipelineStage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const handleDragLeave = (stage: PipelineStage) => {
    if (dragOverStage === stage) setDragOverStage(null);
  };

  const handleDrop = async (e: React.DragEvent, targetStage: PipelineStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const idStr = e.dataTransfer.getData('text/plain');
    const id = idStr ? parseInt(idStr, 10) : draggedId;
    setDraggedId(null);
    if (!id) return;
    const item = pipelines.find((p) => p.id === id);
    if (!item || item.stage === targetStage) return;

    // Optimistic update
    const prev = pipelines;
    setPipelines((arr) =>
      arr.map((p) => (p.id === id ? { ...p, stage: targetStage } : p))
    );

    const result = await dealPipelineService.advanceStage(id, targetStage);
    if (result.error) {
      toast.error(result.error);
      setPipelines(prev);
    } else if (result.data) {
      setPipelines((arr) => arr.map((p) => (p.id === id ? result.data! : p)));
      toast.success(`Moved to ${STAGE_CONFIG[targetStage].label}`);
    }
  };

  const handleFilter = <K extends keyof PipelineListFilters>(
    key: K,
    value: PipelineListFilters[K] | ''
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' || value === undefined ? undefined : value,
    }));
  };

  const openCreate = async () => {
    setCreateOpen(true);
    setSelectedMatchId(undefined);
    setMatchesLoading(true);
    const result = await matchService.fetchMatches({ size: 200 });
    setMatchesLoading(false);
    if (result.data) setMatches(result.data.matches);
  };

  const handleCreate = async () => {
    setCreating(true);
    const result = await dealPipelineService.createPipeline(
      selectedMatchId ? { match_id: selectedMatchId } : {}
    );
    setCreating(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Pipeline created');
    setCreateOpen(false);
    loadPipelines();
  };

  const renderCard = (p: DealPipeline) => {
    const investorName = p.investor?.full_name || `Investor #${p.investor_id}`;
    const targetName =
      p.project?.project_name ||
      p.startup?.name ||
      (p.project_id
        ? `Project #${p.project_id}`
        : p.startup_id
        ? `Startup #${p.startup_id}`
        : '—');
    const matchScore = p.match?.overall_score;

    return (
      <Link
        key={p.id}
        to={`/deal-pipeline/${p.id}`}
        draggable
        onDragStart={(e) => handleDragStart(e, p.id)}
        onDragEnd={handleDragEnd}
        className={`block bg-white rounded-lg border border-gray-200 p-2.5 hover:shadow-md transition-shadow ${
          draggedId === p.id ? 'opacity-40' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">{investorName}</div>
            {p.investor?.firm_name && (
              <div className="text-xs text-gray-500 truncate">{p.investor.firm_name}</div>
            )}
          </div>
          {matchScore != null && (
            <div className={`text-sm font-bold flex-shrink-0 ${scoreColor(matchScore)}`}>
              {Math.round(matchScore)}
            </div>
          )}
        </div>

        <div className="flex items-center text-xs text-gray-600 mt-1.5">
          <ArrowRight className="w-3 h-3 mr-1 text-gray-400" />
          <span className="truncate">{targetName}</span>
        </div>

        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {p.outcome && (
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${outcomeBadge(p.outcome)}`}>
              {p.outcome}
            </span>
          )}
          {p.days_in_pipeline != null && (
            <span className="inline-flex items-center text-xs text-gray-500">
              <Clock className="w-3 h-3 mr-1" />
              {p.days_in_pipeline}d
            </span>
          )}
        </div>
      </Link>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deal Pipeline</h1>
            <p className="text-sm text-gray-600 mt-1">
              10-stage funnel from suggested matches to closed deals.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/deal-pipeline/analytics"
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Link>
            <button
              onClick={openCreate}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create pipeline
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <select
              value={filters.investor_id ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  investor_id: v === '' ? undefined : parseInt(v, 10),
                }));
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All investors</option>
              {investors.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.full_name}
                  {inv.firm_name ? ` (${inv.firm_name})` : ''}
                </option>
              ))}
            </select>

            <select
              value={filters.project_id ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  project_id: v === '' ? undefined : parseInt(v, 10),
                }));
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All projects/startups</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.project_name}
                </option>
              ))}
            </select>

            <select
              value={filters.outcome || ''}
              onChange={(e) =>
                handleFilter('outcome', e.target.value as PipelineOutcome | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All outcomes</option>
              {OUTCOME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={filters.date_from || ''}
              onChange={(e) => handleFilter('date_from', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="From"
            />

            <input
              type="date"
              value={filters.date_to || ''}
              onChange={(e) => handleFilter('date_to', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="To"
            />
          </div>
        </div>

        {/* Kanban */}
        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading pipeline...
          </div>
        ) : pipelines.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <GitMerge className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No pipelines yet</h2>
            <p className="text-sm">
              Promote a match into the deal pipeline to get started.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-3" style={{ minWidth: `${PIPELINE_STAGES.length * 240}px` }}>
              {PIPELINE_STAGES.map((stage) => {
                const cfg = STAGE_CONFIG[stage];
                const items = grouped[stage] || [];
                const isOver = dragOverStage === stage;
                return (
                  <div
                    key={stage}
                    onDragOver={(e) => handleDragOver(e, stage)}
                    onDragLeave={() => handleDragLeave(stage)}
                    onDrop={(e) => handleDrop(e, stage)}
                    className={`w-[230px] flex-shrink-0 rounded-xl border-2 ${cfg.border} ${cfg.bg} flex flex-col ${
                      isOver ? 'ring-2 ring-blue-400' : ''
                    }`}
                  >
                    <div className="p-3 border-b border-gray-200 bg-white rounded-t-xl">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                        <h3 className={`flex-1 font-semibold text-sm ${cfg.text}`}>
                          {cfg.label}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-white ${cfg.text} border ${cfg.border}`}>
                          {items.length}
                        </span>
                      </div>
                    </div>
                    <div className="p-2 space-y-2 min-h-[200px] max-h-[600px] overflow-y-auto flex-1">
                      {items.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-xs">
                          Drop here
                        </div>
                      ) : (
                        items.map(renderCard)
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Create modal */}
      {createOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !creating && setCreateOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Create pipeline</h2>
                <p className="text-xs text-gray-600 mt-1">
                  Promote an existing match into the deal pipeline.
                </p>
              </div>
              <button
                onClick={() => setCreateOpen(false)}
                disabled={creating}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Match (optional)
              </label>
              {matchesLoading ? (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading matches...
                </div>
              ) : (
                <select
                  value={selectedMatchId ?? ''}
                  onChange={(e) =>
                    setSelectedMatchId(
                      e.target.value ? parseInt(e.target.value, 10) : undefined
                    )
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">No match (blank pipeline)</option>
                  {matches.map((m) => {
                    const inv = m.investor?.full_name || `Investor #${m.investor_id}`;
                    const target =
                      m.project?.project_name ||
                      m.startup?.name ||
                      (m.project_id ? `Project #${m.project_id}` : `Startup #${m.startup_id}`);
                    return (
                      <option key={m.id} value={m.id}>
                        {inv} → {target} ({Math.round(m.overall_score)})
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setCreateOpen(false)}
                disabled={creating}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default DealPipelinePage;
